import React, { useContext } from 'react';
import DraggableChipList, {
  IItem as IChip,
  IItem,
} from '../../../../../../../BaseComponents/Chip/DraggableChip/EDDraggableChipList';
import { IColumn } from '../../Queries/EkonomiskAnalysSettingsQuery/EkonomiskAnalysSettingsQuery';
import { ekanalys_ekanalys_columns } from '../../Queries/EkonomiskAnalysSettingsQuery/_QueryTypes/ekanalys';
import Logger from '../../../../../../../Utils/Logger';
import { EkonomiskAnalysSideMenuContext } from '../SideMenu/Context/EkonomiskAnalysSideMenuContext';
import EkonomiskAnalysColumnCaptionQuery from '../../Queries/EkonomiskAnalysColumnCaptionQuery/EkonomiskAnalysColumnCaptionQuery';
import EkonomiskAnalysRadbaseradColumnChip from '../SideMenu/Components/PeriodOchKolumner/ColumnChip/EkonomiskAnalysRadbaseradColumnChip';
import { TECKENVAND_KOLUMN } from '../../Const/EkonomiskAnalysConsts';
import { clone } from 'ramda';

interface IProps {
  columns: IColumn[];
  onUpdate: (columns: IColumn[]) => void;
  onColumnClick: (clickedColumn: ekanalys_ekanalys_columns) => void;
  disableColumnEdit: boolean;
}

interface IInnerProps extends IProps {
  isLopandeAnalys: boolean;
}

const findColumn = (item: IItem, columns: IColumn[]) => {
  return columns.find(col => col.id === item.id);
};

/**
 * En lista över kolumner. Deras interna ordning kan ändras genom att dra i chippen.
 */
const EkonomiskAnalysColumnsBase: React.FunctionComponent<
  React.PropsWithChildren<IInnerProps>
> = props => {
  const onSelectedSumsUpdate = (chipList: IChip[]) => {
    let columns: IColumn[] = [];

    chipList.forEach(x => {
      const column = props.columns.find(y => y.id === x.id);

      if (column) {
        columns.push(column);
      }
    });

    props.onUpdate(columns);
  };

  const onClick = (item: IChip) => {
    const column = findColumn(item, props.columns);
    if (column == null) {
      Logger.error(new Error(`Could not find column with name: ${item.name}`));
    } else {
      props.onColumnClick(column.data);
    }
  };

  const onHide = (item: IItem) => {
    const columnsCopy = clone(props.columns);
    const col = findColumn(item, columnsCopy);
    if (col) {
      col.data.HIDE_COLUMN = true;
      props.onUpdate(columnsCopy);
    }
  };

  return (
    <>
      {props.isLopandeAnalys && (
        <DraggableChipList
          clickDisabled={props.disableColumnEdit}
          disabledReason={
            props.disableColumnEdit ? 'Ogiltig period' : undefined
          }
          items={props.columns.map(
            (x): IItem => ({
              name: x.displayLabel || x.name,
              id: x.id,
              alternateAvatar: x.data.VAND_TECKEN ? 'reverse' : undefined,
              avatarTooltip: x.data.VAND_TECKEN ? TECKENVAND_KOLUMN : undefined,
              hide: x.data.HIDE_COLUMN == null ? false : x.data.HIDE_COLUMN,
            })
          )}
          onItemsUpdate={onSelectedSumsUpdate}
          canDelete
          onClick={onClick}
          dragHandle
          onDelete={onHide}
        />
      )}
      {!props.isLopandeAnalys &&
        props.columns
          .filter(col => !col.data.HIDE_COLUMN)
          .map(column => (
            <EkonomiskAnalysRadbaseradColumnChip
              key={column.id}
              column={column}
            />
          ))}
    </>
  );
};

const EkonomiskAnalysColumns: React.FunctionComponent<
  React.PropsWithChildren<IProps>
> = props => {
  const { newAnalysSettings, selectedAnalys, isLopandeAnalys } = useContext(
    EkonomiskAnalysSideMenuContext
  );

  return (
    <div>
      <EkonomiskAnalysColumnCaptionQuery
        onUpdate={props.onUpdate}
        columns={props.columns}
        codePrivilegeId={selectedAnalys ? selectedAnalys.codePrivilege.id : ''}
        analysName={selectedAnalys ? selectedAnalys.analys.name : ''}
        dates={newAnalysSettings.dates}
        isInterval={newAnalysSettings.isInterval}
      >
        {oldColumns => {
          const colsUsed =
            oldColumns === undefined
              ? props.columns
              : props.columns.map(col => {
                  const colCaption = oldColumns.find(
                    oldCol => oldCol.id === col.id
                  );
                  if (colCaption) {
                    return colCaption;
                  } else {
                    return col;
                  }
                });
          return (
            <EkonomiskAnalysColumnsBase
              columns={colsUsed}
              onUpdate={props.onUpdate}
              onColumnClick={props.onColumnClick}
              isLopandeAnalys={isLopandeAnalys}
              disableColumnEdit={props.disableColumnEdit}
            />
          );
        }}
      </EkonomiskAnalysColumnCaptionQuery>
    </div>
  );
};

export default EkonomiskAnalysColumns;
